import loginStyles from "./login.module.css";
import { ChangeEvent, useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OnboardingLayout from "../../components/onboardingLayout/onboardingLayout";
import { useAppSelector } from "../../services/hooks";
import { errorUserSelector } from "../../services/selectors/auth.selectors";
import {
  useAuthWithoutLoginMutation,
  useLoginGoogleMutation,
  useSendCodeWithoutLoginMutation,
} from "../../services/api/auth.api";
import Tooltip from "../../components/tooltip/tooltip";
import { useGoogleLogin } from "@react-oauth/google";
import { TLoginGoogleRequest } from "../../services/types/auth.types";
import { userSelector } from "../../services/selectors/user.selectors";

function LoginSmsCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/home";

  const user = useAppSelector(userSelector);
  const loginError = useAppSelector(errorUserSelector);

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const initialState = ["", "", "", "", "", ""];
  const [nums, setNums] = useState(initialState);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSendCodeTooltip, setShowSendCodeTooltip] = useState(false);
  const [isSendDisabled, setIsSendDisabled] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);


  const [sendTokenToServer, { isError: isSendTokenError }] =
    useLoginGoogleMutation();
  const [authWithoutLogin, { isError }] = useAuthWithoutLoginMutation();
  const [sendCodeWithoutLogin, { isError: isSendAgainError }] =
    useSendCodeWithoutLoginMutation();

  useEffect(() => {
    if (!user.phone) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (nums.every((num) => num !== "")) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nums]);

  // Function to focus on the next input
  const focusNextInput = (index: number) => {
    const nextIndex = index + 1;
    const nextInputRef = inputRefs[nextIndex];
    if (nextInputRef && nextInputRef.current) {
      nextInputRef.current.focus();
    }
  };
  const handleNumChange = async (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    focusNextInput(index);
    const limit = 1;
    try {
      await setNums(
        nums.map((num, index) =>
          index.toString() === e.target.id
            ? e.target.value.slice(0, limit)
            : num
        )
      );
    } catch (err) {
      console.log(err, nums);
    }
  };

  const handleSubmit = () => {
    authWithoutLogin({
      code: nums.join(""),
      phone: user.phone,
      roleId: "1",
    }).then((response) => {
      if ("data" in response && response.data.success) {
        navigate(from, { replace: true });
      }
    });

    setNums(initialState);
  };

  const sendCodeAgain = () => {
    if (isSendDisabled) return;

    sendCodeWithoutLogin({ phone: user.phone, roleId: "1", code: "0" })
      .then(() => {
        setShowSendCodeTooltip(true);
        setIsSendDisabled(true);
        setCountdown(30);
      })
      .catch((err) => {
        console.log(err);
      });
    setTimeout(() => {
      setShowSendCodeTooltip(false);
    }, 1500);
    setTimeout(() =>
      setIsSendDisabled(false),
      30000);

  };


  useEffect(() => {
    if (countdown === null) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev && prev > 1) return prev - 1;
        clearInterval(interval);
        setIsSendDisabled(false);
        return null;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);


  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      sendTokenToServer(codeResponse as TLoginGoogleRequest)
        .then((response) => {
          if ("data" in response && response.data.success) {
            // navigate("/home");
          }
        })
        .catch((err) => {
          console.log(err);
          console.log(loginError);
        });
    },
  });

  return (
    <OnboardingLayout>
      <article className={loginStyles.login}>
        <button
          type={"button"}
          onClick={() => navigate("/login")}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={loginStyles.login__arrow}
        >
          <Tooltip isShown={showTooltip}>למסך הקודם</Tooltip>
        </button>
        <h1
          className={`${loginStyles.login__title} ${loginStyles.login__title_sms}`}
        >
          התגעגענו
        </h1>
        <p
          className={`${loginStyles.login__text} ${loginStyles.login__text_small}`}
        >
          שלחנו קוד אימות בSMS לנייד שמסתיים ב{user.phone.slice(-3)}
        </p>
        <label htmlFor="num1" className={loginStyles.login__enterCodeLabel}>
          הקלד קוד אימות
        </label>
        <ul className={loginStyles.login__enterCode}>
          {nums.map((num, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              onChange={(e) => handleNumChange(e, index)}
              type="number"
              name={index.toString()}
              id={index.toString()}
              className={loginStyles.login__number}
              placeholder="__"
              value={nums[index]}
              autoFocus={index === 0}
            />
          ))}
        </ul>
        {(isError || isSendAgainError || isSendTokenError) && (
          <p className={`${loginStyles.login__invalid_message}`}>
            {loginError}
          </p>
        )}
        <p className={loginStyles.login__sendAgain}>
          לא קיבלת קוד?
          <span
            className={`${loginStyles.login__sendAgain_underlined} hover`}
            onClick={sendCodeAgain}
            style={{ pointerEvents: isSendDisabled ? 'none' : 'auto', opacity: isSendDisabled ? 0.6 : 1 }}

          >
            שלח מחדש
            <Tooltip isShown={showSendCodeTooltip}>שלחנו שוב</Tooltip>
          </span>
          {isSendDisabled && countdown !== null && (
            <span className={loginStyles.login__countdown}>
              שליחה נוספת תתאפשר בעוד {countdown} שניות
            </span>
          )}
        </p>

        <div className={loginStyles.login__bottomBar}>
          <p className={loginStyles.login__alternative}>או</p>
          <div className={loginStyles.login__social}>
            <ul
              className={`${loginStyles.login__socialList} ${loginStyles.login__socialList_column}`}
            >
              <li>
                <button
                  type="button"
                  onClick={() => loginWithGoogle()}
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
                >
                  <span
                    className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_google}`}
                  />
                  התחבר דרך גוגל
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
                >
                  <span
                    className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_apple}`}
                  />
                  התחבר דרך אפל
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
                >
                  <span
                    className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_facebook}`}
                  />
                  התחבר דרך פייסבוק
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div>
          <p className={loginStyles.login__conditions}>שימוש באפליקציה תלוי</p>
          <Link className={loginStyles.login__link} to="/terms">
            בתקנון השימוש
          </Link>
        </div>
        {/*<Urgent />*/}
      </article>
    </OnboardingLayout>
  );
}

export default LoginSmsCode;
